import React from 'react';
import PropTypes from 'prop-types';
import { JD } from 'apps/home/company/careers/jd';

import Heading from 'components/Heading';
import Paragraph from 'components/Paragraph';

const customLinkBuilder = id =>
  `https://wikifactory.bamboohr.com/jobs/view.php?id=${id}&source=other`;

// Introduction after 26 April 2021
const Introduction = () => (
  <>
    <Heading level='3' size='medium'>
      About Us
    </Heading>
    <p>
      Wikifactory is the fastest place on the web to get a physical product made
      with just a laptop and an internet connection. Zero training and zero
      configuration required. We have a global community of over 90,000
      engineers, designers, makers and over 100 hardware startups across 190
      countries. They have made over 4,500 products across several industries:
      drones for reforestation, electric vehicles and future tech (Hyperloop
      pods and flying cars), agri-tech solutions, smart furniture and IoT
      devices, robotics, biotech lab equipment, and most recently medical
      devices and PPE during the Covid-19 crisis.
    </p>
    <p>
      As a SaaS platform for collaborative product development of physical
      things, we are{' '}
      <b>
        uniquely positioned for discussing CAD design and engineering topics
      </b>
      . We have a powerful 3D file embed feature which makes it possible to
      directly view and inspect CAD designs of over 30+ formats directly in
      content on the browser, without the need of a plug-in or extension. In
      addition we have launched{' '}
      <b>
        a first-of-its kind collaborative 3D file viewer for live, on-the-go,
        product development between virtual teams
      </b>{' '}
      on an agile web-based and open-source PLM system.
    </p>
    <p>
      With these web publishing tools and a growing list of exciting end-to-end
      product innovations happening at Wikifactory, we are building{' '}
      <b>
        an exceptional community of interest and of practice on our platform
      </b>{' '}
      that helps provide product developers of all types (individual designers &
      engineers, SMBs and enterprises) real value by removing the barriers
      (complex and expensive PLM tools and systems) and removing the common
      process challenges that can distract from efficient, timely and valuable
      product development. As such our system accelerates product innovation and
      supports prototyping and production to scale.
    </p>
    <p>
      At Wikifactory, <b>we are rethinking manufacturing for the digital age</b>
      . Building an Internet of Production to match the internet of knowledge.
      With offices in <b>Madrid, Copenhagen, London and Shenzhen</b>, our team
      is truly global.
    </p>
    <p>
      Our platform includes a 3D Viewer that can visualise more than 30 file
      formats; a version control drive to manage changes; a documentation system
      and even social functionalities including comments, views, shares, likes,
      issue tracking for feedback, and forums for debate. It also has a built-in
      publishing tool for blogs by users on which our community can not only
      publish A/V content but also CAD files. Join our community and build
      something today.
    </p>
  </>
);

// Introduction before 26 April 2021
const Intro = () => (
  <>
    <h3>A little bit about us</h3>
    <p>
      <b>Wikifactory</b> is where ideas get made. We&apos;re the world’s first
      social platform for collaborative product development which enables teams
      to design together, prototype faster, and manufacture smarter. But it is
      also much more than that. We&apos;re a fast-growing start-up is on a
      mission to reshape the future of design and production to become the
      leader in The Internet of Production (IoP).
    </p>
    <p>
      Inspired by the workflows of agile and open source software development,
      we are building the tools to accelerate the development of physical
      products. Our platform offers a 3D CAD Viewer compatible with over 30 file
      formats, a documentation system and version control drive, and a feedback
      system. It is also a web publishing tool allowing the community to publish
      Stories (our version of a blog) and has social functionalities including
      likes, views, shares and forums.
    </p>
    <p>
      Our community includes designers, engineers, makers, hardware startups,
      teachers, students and other product innovators. Covering industries,
      including robotics, drones, electric vehicles, 3d printers, biotech,
      agri-tech, smart furniture, and medical supplies for Covid-19, our
      community is spread across 190 countries and has published thousands of
      meaningful physical products that solve real challenges, with just a
      laptop and an internet connection. By connecting all the different
      elements of the product development lifecycle in one seamless online
      system, we’re building the Internet of Production.
    </p>
    <p>
      Wikifactory is headquartered in Denmark with people in Madrid, Shenzhen
      and London.
    </p>
    <p>
      <a href='https://wikifactory.com'>www.wikifactory.com</a>
    </p>
  </>
);

const WhyWork = () => (
  <>
    <Heading level={3} size='medium'>
      Why Work at Wikifactory
    </Heading>
    <ul>
      <li>
        Join an exciting start-up with ambitious plans in a fast growing,
        multi-billion dollar industry
      </li>
      <li>
        Apply your experience in a disruptive technology area that is impacting
        every aspect of product development
      </li>
      <li>
        Gain unparalleled access to innovation communities in China and around
        the World
      </li>
    </ul>
  </>
);

const HowToApply = ({ position }) => (
  <>
    <Heading level='3' size='medium'>
      How to Apply
    </Heading>
    <Paragraph>
      Please send an email to{' '}
      <a href={`mailto:talent@wikifactory.com?Wikifactory | ${position}`}>
        talent@wikifactory.com
      </a>{' '}
      and include:
    </Paragraph>
    <ul>
      <li>
        A brief note introducing yourself and explaining why we caught your
        interest
      </li>
      <li>Your resume or a link to it (LinkedIn is fine)</li>
      <li>
        A link to code you’re particularly proud of. Provide a description of
        what it does and why you’re proud of it.
      </li>
    </ul>
  </>
);

HowToApply.propTypes = {
  isExpanded: PropTypes.bool,
  jobPath: PropTypes.string,
  position: PropTypes.string,
};

export const SeniorFullStack = props => (
  <JD
    title='Senior Full Stack Engineer'
    isExpanded={props.isExpanded}
    jobPath={props.jobPath}
    salary='Madrid    Full-time'
    key='seniorFullStackEngineer'
    customLink={customLinkBuilder('27')}
  >
    <p>
      Are you a talented Senior Full Stack Engineer with 5 years experience and
      fluent spoken and written English looking for an exciting role where you
      can take your skills to the next level? Would you like to join a
      fast-growing international startup and shape the future of how products
      are designed, prototyped and manufactured? If this sounds interesting,
      then we&apos;d love to hear from you.
    </p>
    <Intro />
    <h3>The Role</h3>
    <p>
      Wikifactory’s client-side applications are just as technical as our
      backend applications. We are looking for a Full-Stack engineer to focus on
      front-end engineering.
    </p>
    <p>
      The ideal candidate will be highly technical and enjoy solving challenging
      problems, such as:
    </p>
    <ul>
      <li>
        Building user-facing features with everything from database models to
        resolvers and queries.
      </li>
      <li>
        Solving collaborative text editing or similar algorithmic merging
        problems.
      </li>
      <li>
        Improving the client’s performance via profiling and benchmarking.
      </li>
      <li>Enhancing reliability by developing testing infrastructures.</li>
      <li>Developing API integrations.</li>
      <li>
        Creating server-side rendering infrastructures to improve load times.
      </li>
      <li>Visualising and/or parsing PCB files.</li>
    </ul>
    <p>
      As our future Full-Stack engineer, you will play a key role in (1)
      enhancing our social networking capabilities to better serve the needs of
      product designers and manufacturers, (2) improving our version control and
      continuous integration system for physical products as well as (3)
      designing and creating a revolutionary open marketplace for digital
      manufacturing - set to change the way people prototype hardware products
      and manufacture them.
    </p>
    <h3>Our Stack</h3>
    <p>
      We use carefully selected technologies to build maintainable systems, so
      we can continuously make progress on a solid foundation. Our stack
      includes
    </p>
    <ul>
      <li>ES6/React front-end with styled-components.</li>
      <li>Apollo/GraphQL data queries and mutations.</li>
      <li>MobX for data stores and processing logic.</li>
      <li>Slate.js based rich-media text editor.</li>
      <li>Puppeteer for server-side rendering.</li>
      <li>Python and Golang backend environments.</li>
      <li>Postgres and Dgraph (Graph database).</li>
      <li>WebAssembly based 3D viewer.</li>
      <li>Github, ZenHub, Slack, Notion</li>
    </ul>
    <Heading level={3} size='medium'>
      Qualifications
    </Heading>
    <ul>
      <li>Strong software engineering background.</li>
      <li>
        Minimum 5 years experience with JavaScript/ES6, HTML5 and Web APIs
      </li>
      <li>Fluent in English with strong technical communication skills.</li>
    </ul>
    <h3>Why work at Wikifactory</h3>
    <p>
      Would you like to join an international and fast growing Startup where you
      can play an important part in shaping its future? How about an opportunity
      to take part of next-generation social impact solutions in a highly
      collaborative work environment, where space for professional development
      is taken very seriously? Interesting, right?
    </p>
    <h3>Renumeration:</h3>
    <p>Depending on your experience.</p>
    <HowToApply position='Senior Full Stack Engineer' />
    <WhyWork />
  </JD>
);

SeniorFullStack.propTypes = {
  isExpanded: PropTypes.bool,
  jobPath: PropTypes.string,
};

export const SeniorBackend = props => (
  <JD
    title='Senior Back End Engineer (Python)'
    isExpanded={props.isExpanded}
    jobPath={props.jobPath}
    salary='Madrid    Full-time'
    key='seniorBackend'
    customLink={customLinkBuilder('28')}
  >
    <p>
      Are you a talented Senior Back End Engineer with 5 years experience, and
      fluent spoken and written English looking for an exciting role where you
      can take your skills to the next level? Would you like to join a
      fast-growing international startup and shape the future of how products
      are designed, prototyped and manufactured? If this sounds interesting,
      then we&apos;d love to hear from you.
    </p>
    <Intro />
    <h3>The Role</h3>
    <p>
      You will be playing a lead role in managing and improving our backend
      infrastructure (currently AWS centric), maintaining and improving our
      existing backend applications in Python and Golang. You will be
      responsible for developing new capabilities to enable our users to work
      together more effectively on improving product designs and specifications
      as well as manufacture the products they design together.
    </p>
    <h3>Qualifications</h3>
    <ul>
      <li>
        Minimum 3 years strong experience building applications in Python and/or
        Golang.
      </li>
      <li>Strong computer science knowledge (University not required)</li>
      <li>Knowledge of linux system administration</li>
      <li>AWS or similar cloud computing environment</li>
      <li>Ansible or similar deployment automation tools</li>
      <li>
        A fascination with digital fabrication technologies or interest in
        hardware development
      </li>
      <li>Strong command of English is required</li>
    </ul>
    <Heading level={3} size='medium'>
      Nice-to-Haves
    </Heading>
    <Paragraph>Experience with:</Paragraph>
    <ul>
      <li>Documenting, releasing and maintaining open source libraries</li>
      <li>SQLAlchemy, Flask, GraphQL</li>
      <li>JavaScript/ES6</li>
      <li>PostgresSQL, Dgraph</li>
    </ul>
    <Paragraph>Interest or Skills in:</Paragraph>
    <ul>
      <li>3D rendering and 3D application development</li>
      <li>Robotics, CNC, g-code, etc.</li>
      <li>Security best practices for web and mobile</li>
    </ul>
    <h3>Renumeration:</h3>
    <p>Depending on your experience.</p>
    <HowToApply position='Senior Back End Engineer' />
    <WhyWork />
  </JD>
);

SeniorBackend.propTypes = {
  isExpanded: PropTypes.bool,
  jobPath: PropTypes.string,
};

export const SeniorBackendGolang = props => (
  <JD
    title='Senior Back End Engineer (Golang)'
    isExpanded={props.isExpanded}
    jobPath={props.jobPath}
    salary='Madrid    Full-time'
    key='seniorBackend'
    customLink={customLinkBuilder('29')}
  >
    <p>
      Are you a talented Senior Back End Engineer with 5 years experience, and
      fluent spoken and written English looking for an exciting role where you
      can take your skills to the next level? Would you like to join a
      fast-growing international startup and shape the future of how products
      are designed, prototyped and manufactured? If this sounds interesting,
      then we&apos;d love to hear from you.
    </p>
    <Intro />
    <h3>The Role</h3>
    <p>
      You will be playing a lead role in managing and improving our backend
      infrastructure (currently AWS centric), maintaining and improving our
      existing backend applications in Python and Golang. You will be
      responsible for developing new capabilities to enable our users to work
      together more effectively on improving product designs and specifications
      as well as manufacture the products they design together.
    </p>
    <h3>Qualifications</h3>
    <ul>
      <li>
        Minimum 3 years strong experience building applications in Python and/or
        Golang.
      </li>
      <li>Strong computer science knowledge (University not required)</li>
      <li>Knowledge of linux system administration</li>
      <li>AWS or similar cloud computing environment</li>
      <li>Ansible or similar deployment automation tools</li>
      <li>
        A fascination with digital fabrication technologies or interest in
        hardware development
      </li>
      <li>Strong command of English is required</li>
    </ul>
    <Heading level={3} size='medium'>
      Nice-to-Haves
    </Heading>
    <Paragraph>Experience with:</Paragraph>
    <ul>
      <li>Documenting, releasing and maintaining open source libraries</li>
      <li>SQLAlchemy, Flask, GraphQL</li>
      <li>JavaScript/ES6</li>
      <li>PostgresSQL, Dgraph</li>
    </ul>
    <Paragraph>Interest or Skills in:</Paragraph>
    <ul>
      <li>3D rendering and 3D application development</li>
      <li>Robotics, CNC, g-code, etc.</li>
      <li>Security best practices for web and mobile</li>
    </ul>
    <h3>Renumeration:</h3>
    <p>Depending on your experience.</p>
    <HowToApply position='Senior Back End Engineer Golang' />
    <WhyWork />
  </JD>
);

SeniorBackendGolang.propTypes = {
  isExpanded: PropTypes.bool,
  jobPath: PropTypes.string,
};

export const JuniorGraphicDesigner = props => (
  <JD
    title='Junior Graphic Designer'
    isExpanded={props.isExpanded}
    jobPath={props.jobPath}
    salary='Madrid    Full-time'
    key='juniorGraphicDesigner'
  >
    <p>
      Are you a talented and creative Junior Graphic Designer looking for a new
      and exciting role to design marketing & communications content for an
      exciting technology company? Would you like to join an international and
      fast growing Startup where you can play an important part in shaping its
      future? How about an opportunity to take part in next-generation social
      impact solutions in a highly collaborative and virtual working
      environment, where space for professional development is taken very
      seriously? If this sounds interesting, then we&apos;d love to hear from
      you.
    </p>
    <Intro />
    <h3>The Role</h3>
    <p>
      As a Junior Graphic Designer, you will work within the Marketing &
      Communications team of seven people and report directly into the Head of
      Marketing & Communications. You will be involved in both conceiving ideas
      and taking design briefs for external and internal communications
      materials. You will have an active interest and knowledge of social media
      and content marketing trends, PR, Branding and digital advertising. You
      will have excellent time-management skills, a keen eye for detail,
      knowledge and experience of a variety of design software, and photo
      editing tools.
    </p>
    <p>
      A proactive and flexible team player, you will be able to work in a
      fast-paced, virtual working environment, developing both template and
      bespoke content.
    </p>
    <p>
      You will be skilled and quick at learning and implementing our Brand
      Guidelines to create a variety of artwork for all communications channels,
      including, for example:
    </p>
    <ul>
      <li>
        Digital assets: social media artwork in the correct formats, GIFs and IG
        stickers, marketing emails, website banners, pop-up posts, custom
        landing page assets, digital ads (Google Ads, Native Ads), webinar
        assets (such as virtual backgrounds, designed surveys, artwork for
        listings pages), etc.
      </li>
      <li>
        Corporate collateral: advertorials, press releases, e-books,
        whitepapers, infographics (including animated infographics), case study
        slides, internal reports, shareholder reports, company presentations,
        commercial brochures and decks, business cards, etc.
      </li>
      <li>
        Exhibition materials: virtual and print posters, displays, invitations,
        flyers, conference presentations, selecting stock imagery etc.
      </li>
      <li>
        Extending our iconography and illustration assets, as well as knowledge
        of creating look-books and OOH advertising would be nice-to-haves.
      </li>
    </ul>
    <h3>Responsibilities</h3>
    <ul>
      <li>Study design briefs and determine requirements</li>
      <li>Schedule projects</li>
      <li>Conceptualize visuals based on requirements</li>
      <li>Prepare rough drafts and present ideas</li>
      <li>Develop illustrations, logos and other designs using software</li>
      <li>Use the appropriate colors and layouts for each graphic</li>
      <li>Work with relevant team to produce final design</li>
      <li>Test graphics across various media</li>
      <li>Amend designs after feedback</li>
      <li>
        Ensure final graphics and layouts are visually appealing and on-brand
      </li>
      <li>Keeping up to date with emerging technologies.</li>
    </ul>
    <h3>Requirements</h3>
    <ul>
      <li>Proven graphic designing experience</li>
      <li>A strong portfolio of illustrations or other graphics</li>
      <li>
        Knowledge of Adobe PhotoShop, Illustrator, Sketch, InDesign, and other
        graphic design software
      </li>
      <li>A keen eye for aesthetics and details</li>
      <li>Excellent communication and interpersonal skills</li>
      <li>Ability to work methodically and meet deadlines</li>
      <li>
        Experience of working in-house for a brand alongside web developers,
        user engagement, marketing, branding and PR specialists at an agency or
        publisher.
      </li>
      <li>Degree in Design, Fine Arts or related field is a plus</li>
    </ul>
    <p>Sound interesting? Then we&apos;d like to hear from you!</p>
    <h3>How to Apply</h3>
    <p>
      Please send an email to{' '}
      <a href='mailto:talent@wikifactory.com?Wikifactory | Junior Graphic Designer'>
        talent@wikifactory.com
      </a>{' '}
      and include:
    </p>
    <ul>
      <li>
        A brief note introducing yourself and explaining why we caught your
        interest
      </li>
      <li>Your resume or a link to it (LinkedIn is fine)</li>
      <li>
        A link to a portfolio you’re particularly proud of. Provide a
        description of what it does and why you’re proud of it
      </li>
    </ul>
    <WhyWork />
  </JD>
);

JuniorGraphicDesigner.propTypes = {
  isExpanded: PropTypes.bool,
  jobPath: PropTypes.string,
};

export const SeniorUXDesigner = props => (
  <JD
    title='Senior UX Designer'
    isExpanded={props.isExpanded}
    jobPath={props.jobPath}
    salary='Madrid    Full-time'
    key='seniorUXDesigner'
  >
    <p>
      Are you a talented Senior UX designer with 5 years experience, and fluent
      spoken and written English looking for an exciting role where you can take
      your skills to the next level? Would you like to join a fast-growing
      international startup and shape the future of how products are designed,
      prototyped and manufactured? If this sounds interesting, then we&apos;d
      love to hear from you.
    </p>
    <Intro />
    <h3>The Role</h3>
    <p>
      As Senior UX Designer, you will be working closely with the Head of
      Product at a strategic level to understand and frame what we need to
      build. You will be driving ongoing user research, designing low/medium
      fidelity interactive prototypes and testing these with design partners and
      customers, as well as collaborating with a UI Designer and UI Developer
      with the goal to create best in class product experiences. More
      specifically you will:
    </p>
    <ul>
      <li>
        Execute end-to-end detailed design deliverables as a series of
        experience maps, storyboards, wireframes, and interactive prototypes.
      </li>
      <li>
        Analyse how users interact with our products, and present data-driven
        solutions to increase conversions.
      </li>
      <li>
        Define and conduct user research to truly understand our customers and
        users’ goals and needs.
      </li>
      <li>Validate design and interactive prototypes with design partners.</li>
    </ul>
    <h3>What you bring</h3>
    <ul>
      <li>
        5+ years in UX or Interaction Design with heavy emphasis on both
        human-centred as well as data-driven design methods.
      </li>
      <li>
        An inspiring portfolio showing us how you have researched and crafted
        great experiences, seeing them through to released products.
      </li>
      <li>
        Expert with design tools (e.g. Sketch, Figma, Adobe XD) to produce
        wireframes and high-fidelity interactive prototypes.
      </li>
      <li>
        Ability to understand and distill complex problems into elegant,
        informed solutions.
      </li>
      <li>
        Experience working in an iterative and agile development environment.
      </li>
    </ul>
    <h3>Nice-to-haves</h3>
    <ul>
      <li>Experience designing community platforms and features.</li>
      <li>
        Experience working in Mechanical/Industrial/Electrical Engineering
        contexts.
      </li>
      <li>Experience designing applications for the Manufacturing sector.</li>
    </ul>
    <h3>How to Apply</h3>
    <p>
      Please send an email to{' '}
      <a href='mailto:talent@wikifactory.com?Wikifactory | Senior UX Designer'>
        talent@wikifactory.com
      </a>{' '}
      and include:
    </p>
    <ul>
      <li>
        A brief note introducing yourself and explaining why we caught your
        interest
      </li>
      <li>Your resume or a link to it (LinkedIn is fine)</li>
      <li>
        A link to a portfolio you’re particularly proud of. Provide a
        description of what it does and why you’re proud of it
      </li>
    </ul>
    <WhyWork />
  </JD>
);

SeniorUXDesigner.propTypes = {
  isExpanded: PropTypes.bool,
  jobPath: PropTypes.string,
};

export const SeniorUIDeveloper = props => (
  <JD
    title='Senior UI Developer'
    isExpanded={props.isExpanded}
    jobPath={props.jobPath}
    salary='Remote, Flexible'
    key='seniorUIdeveloper'
    customLink={customLinkBuilder('23')}
  >
    <Introduction />
    <h3>The Role</h3>
    <p>
      You will be working closely with the Head of Product, design and
      engineering team. Responsible for implementing consistently high quality
      user-interfaces based on a growing set of modular, highly flexible and
      re-usable components. More specifically you will be:
    </p>
    <ul>
      <li>
        Informing user interface design proccesses based on your technical
        know-how.
      </li>
      <li>Developing user interface components.</li>
      <li>
        Implementing user flows using re-usable components where possible.
      </li>
      <li>
        Iterating existing user interfaces, improving performance,
        accessibility, semantic structure and usability.
      </li>
      <li>
        Ensuring existing interface components are maintained as designs evolved
        based on UX feedback.
      </li>
      <li>
        Documenting components, empowering frontend engineers to utilise
        existing solutions easily.
      </li>
      <li>Writing and maintaining UI and component tests.</li>
    </ul>
    <h3>What you bring</h3>
    <ul>
      <li>
        5+ years experience implementing modern UIs (SPAs) with modern JS
        (ES6/7, TypeScript, etc)
      </li>
      <li>
        3+ years experience with either React, Angular or Vue (we use React).
      </li>
      <li>
        Experience with either Styles Components, Emotion or CSS Modules (we use
        Styled Components).
      </li>
      <li>Experience with theme based design system development.</li>
      <li>Strong semantic HTML, CSS3+ and SVG knowledge.</li>
      <li>
        Experience with modern UX and developer-handoff tools sign tools such as
        Sketch, Figma, Zeplin.
      </li>
      <li>Excellent communication skills and a team oriented approach.</li>
      <li>
        Enthusiasm for building infrastructure for the next industrial
        revolution
      </li>
    </ul>
    <h3>Nice-to-Haves</h3>
    <ul>
      <li>User experience design and user interface design expertise.</li>
      <li>3D user interface experience.</li>
      <li>Gaming interface development.</li>
    </ul>
    <h3>How to Apply</h3>
    <ul>
      <li>
        A brief note introducing yourself and explaining why we caught your
        interest
      </li>
      <li>Your resume or a link to it (LinkedIn is fine)</li>
      <li>
        A link to code you’re particularly proud of. Provide a description of
        what it does and why you’re proud of it
      </li>
    </ul>
  </JD>
);

SeniorUIDeveloper.propTypes = {
  isExpanded: PropTypes.bool,
  jobPath: PropTypes.string,
};

export const ChiefOperatingOfficer = props => (
  <JD
    title='Chief Operating Officer'
    isExpanded={props.isExpanded}
    jobPath={props.jobPath}
    salary='Madrid    Full-time'
    key='chiefOperatingOfficer'
  >
    <p>
      We’re looking for an experienced, organized and highly-motivated Chief
      Operating Officer to join our growing team. You will be leading our team
      of high-performing professionals to be efficient, productive, and
      effective. While taking responsibility for our operational processes to
      ensure we continue to deliver excellent customer experience. The
      successful candidate will be responsible for developing and supporting
      members of our leadership team. While working very closely with the CEO on
      the development of our culture to ensure our vision is achieved. The role
      reports to the CEO and is a member of the executive team which sets the
      strategy and priorities for the future development of the business.
    </p>
    <Intro />
    <h3>The Role</h3>
    <ul>
      <li>
        Establish and improve processes, tools and practices for scalable
        operations
      </li>
      <li>
        Develop and oversee legal, financial, budget and HR strategies, plans
        and reporting
      </li>
      <li>
        Work with Executive Chairman and CEO to define strategic goals, budgets
        and operational plans
      </li>
      <li>
        Identify the most promising market segments for business growth and
        validate their commercial potential and test viability
      </li>
      <li>
        Drive team execution, Marketing & Comms, Product, R&D, Community
        Hosting, and Commercial
      </li>
      <li>
        Establish and manage performance with KPI tracking and reporting
        practices
      </li>
      <li>
        Oversee timely and solid reporting to Board, Advisory Board,
        Shareholders and Investors
      </li>
    </ul>
    <h3>What you bring</h3>
    <ul>
      <li>
        Proven track record of outstanding performance in a previous complex
        global technology companies and startups (from SaaS companies - an
        advantage)
      </li>
      <li>
        5+ experience managing global enterprise&apos;s human resources,
        finances, budgeting, planning, marketing, operations and strategies.
      </li>
      <li>
        8+ years of leadership experience with a solid strategic planning and
        execution background
      </li>
      <li>
        Strong data & performance driven commercial and marketing background
      </li>
      <li>Proficient business projections & modelling skills</li>
      <li>
        Outstanding verbal and written skills, and experience working with staff
        on all levels
      </li>
      <li>Demonstrated experience of ethical leadership</li>
    </ul>
    <h3>Nice-to-Haves</h3>
    <ul>
      <li>
        Bachelor&apos;s degree in Business Administration or similar ; an MBA -
        an advantage
      </li>
      <li>
        Experience in software companies in CAD, PLM or related industries
      </li>
      <li>Work experience in or with APAC region</li>
    </ul>
    <p>Competitive compensation package, including appealing equity plans.</p>
    <Heading level={3} size='medium'>
      How to Apply
    </Heading>
    <Paragraph>
      Please send an email to{' '}
      <a href='mailto:talent@wikifactory.com?Wikifactory | Chief Operating Officer'>
        talent@wikifactory.com
      </a>{' '}
      and include:
    </Paragraph>
    <ul>
      <li>
        A brief note introducing yourself and explaining why we caught your
        interest
      </li>
      <li>Your resume or a link to it (LinkedIn is fine)</li>
    </ul>
  </JD>
);

ChiefOperatingOfficer.propTypes = {
  isExpanded: PropTypes.bool,
  jobPath: PropTypes.string,
};

export const HeadBusiness = props => (
  <JD
    title='Head of Business Development'
    isExpanded={props.isExpanded}
    jobPath={props.jobPath}
    salary='Copenhagen, Denmark'
    key='headBusiness'
    customLink={customLinkBuilder('26')}
  >
    <p>
      <b>Reports to</b>: Chief Operating Officer (COO)
    </p>
    <p>
      <b>Team</b>: Freedom to shape the team as it grows.
    </p>
    <p>
      <b>Location</b>: our Headquarters in Copenhagen, Denmark + flexible remote
      working.
    </p>
    <p>
      <b>Travel commitments</b>: regular trips to our key locations (Madrid,
      Shenzhen).
    </p>
    <p>
      <b>Package</b>: very attractive package for the right person.
    </p>
    <Introduction />
    <h3>The Role</h3>
    <p>
      The <b>Head of Business Development </b>
      {`at Wikifactory is a key member of the leadership team, with accountability for establishing key partnerships with leading corporate customers globally in the manufacturing, product design and engineering sectors. You will join us at a very key moment in Wikifactory's life, as we experience explosive growth and expect a lot more in the coming months.`}
    </p>
    <p>
      It is an exciting opportunity to{' '}
      <i>build and execute a global Business Development strategy,</i> with the
      goal of rapidly expanding our enterprise customer segment and
      manufacturing marketplace through a professional network that you will
      have developed over the years, plus opportunities through our own network.
    </p>
    <p>
      You will be able to use your knowledge and experience in design and
      engineering software, and manufacturing industries to democratise product
      development and manufacturing. You will also bring a wealth of knowledge
      and a strong professional network to reach out to, and also explore the
      opportunities and leads we are currently working on, as soon as you join
      our team.
    </p>
    <p>
      {`You will have the opportunity to build up Wikifactory's Business Development organisation and partnerships strategy, with the ability to shape the team as we grow. We are heavily focused on China, Europe and the USA as the key markets to develop our enterprise customer segment, as well as to establish key partnerships with manufacturers and professional networks.`}
    </p>
    <p>
      You will partner with many cross-functional groups within Wikifactory,
      including Product, Technology, Growth, Marketing, Business Intelligence,
      Sales, and external Partners. Success in this role will require both{' '}
      <i>
        a strong ability to deeply understand the market in which we operate,
        and ask yourself “how can we build upon this?”
      </i>
    </p>
    <p>
      You’ll also influence how we engage our customers in a human, meaningful
      and value-driven way. This is{' '}
      <i>the chance to shape a highly visible area of the business.</i>
    </p>
    <h3>Responsibilities</h3>
    <ul>
      <li>
        {`Define and execute Wikifactory's global business development strategy.`}
      </li>
      <li>
        Own the business development plan, working deeply with stakeholders to
        execute it.
      </li>
      <li>
        Develop strategic relationships with key organisations (enterprise,
        manufacturers, partnerships and other).
      </li>
      <li>
        Identify and maintain an optimal onboarding strategy for new enterprise
        and manufacturing leads.
      </li>
      <li>
        Ultimate accountable owner for Global Enterprise, Manufacturing and
        Partnerships.
      </li>
    </ul>
    <h3>Essential Requirements - About You</h3>
    <ul>
      <li>
        An ethical leader who effectively works with key stakeholders and team
        members to get the best outcome and inspire others to execute the growth
        strategy in a collaborative way.
      </li>
      <li>
        An expert in the manufacturing, product design or engineering industries
        with an extensive network to reach out to.
      </li>
      <li>
        Experience in building successful multi-channel SaaS business
        development strategies.
      </li>
    </ul>
  </JD>
);

HeadBusiness.propTypes = {
  isExpanded: PropTypes.bool,
  jobPath: PropTypes.string,
};

export const HeadGrowth = props => (
  <JD
    title='Head of Growth and Monetisation'
    isExpanded={props.isExpanded}
    jobPath={props.jobPath}
    salary='Remote, Flexible'
    key='headGrowth'
    customLink={customLinkBuilder('25')}
  >
    <p>
      <b>Reports to</b>: Chief Operating Officer (COO)
    </p>
    <p>
      <b>Team</b>: Digital Community Manager, Content Creators, User Engagement
      and Activation, external agencies. Freedom to shape the team as it grows.
    </p>
    <p>
      <b>Location</b>: Fully remote. Option to work from our offices in Madrid
      (ideal), London and Copenhagen.
    </p>
    <p>
      <b>Travel commitments</b>: trips to our Madrid hub as required (ideally
      monthly).
    </p>
    <p>
      <b>Package</b>: very attractive package for the right person.
    </p>
    <Introduction />
    <h3>The Role</h3>
    <p>
      The <b>Head of Monetisation and Growth </b>at Wikifactory is a key member
      of the leadership team, with accountability for establishing a{' '}
      <i>
        virtuous growth loop cycle (acquisition, retention and monetisation)
      </i>
      . You will join us at a very key moment in {`Wikifactory's`} life, and you
      will be a key driver in our commercial strategy.
    </p>
    <p>
      It is an exciting opportunity to{' '}
      <i>
        build and execute a digital growth, revenue and customer success
        strategy
      </i>{' '}
      with the goal of providing an exceptional, multi-channel engagement
      experience that maximises value for our community users and customers pre
      and post-sale. You will lead digital growth initiatives that in many cases
      will be the {`customer's`} primary means of interaction with Wikifactory.
      Some of your primary targets will include driving engagement, adoption as
      well as future growth of our digital customer base.
    </p>
    <p>
      You’ll own and manage targets associated with user activity, subscription,
      renewal and churn metrics. You will be{' '}
      <i>deep in the data, monitoring and optimisation</i> of your initiatives
      along the way.
    </p>
    <p>
      You will partner with many cross-functional groups within Wikifactory,
      including Product, Technology, Marketing, Business Intelligence, Sales,
      Content Creators and Partners. Success in this role will require both{' '}
      <i>
        a strong ability to understand the digital customer segment and the
        ability to reach out to our community, look at trends, and ask yourself
        “how can we build upon this?”
      </i>
    </p>
    <p>
      You’ll also influence how we engage our customers in a human, meaningful
      and value-driven way. This is{' '}
      <i>the chance to shape a highly visible area of the business.</i>
    </p>
    <p>
      A background in our space would be very useful, but not necessary, along
      with other essential skills like split testing, copywriting or funnel
      building. Knowledge of channels like Facebook ads, social media, PR, SEO,
      PPC and so forth is essential. Organic growth is one of our key drivers
      and demonstrating results in this space is paramount.
    </p>
    <h3>Responsibilities</h3>
    <ul>
      <li>
        Define and execute {`Wikifactory's`} growth, revenue and customer
        success strategy to drive community engagement and incremental revenue
        at a very rapid scale.
      </li>
      <li>
        Define and execute customer lifecycle adoption and engagement
        initiatives to support the growth, revenue and success strategy.
      </li>
      <li>
        Own the commercial strategy, working deeply with stakeholders to execute
        it.
      </li>
      <li>
        Identify and maintain an optimal on-boarding strategy for customers pre
        and post-signup, driving activity, conversions and engagement throughout
        the community.
      </li>
      <li>
        Leverage data and analytics to expand and optimise initiatives, ensuring
        a value-led approach is taken across the teams when setting priorities.
      </li>
      <li>
        Define and drive the right level of keyword optimisation and other
        initiatives to drive traffic and engagement.
      </li>
      <li>Build organic growth through scalable community content creation.</li>
      <li>
        Ultimate accountable owner for B2B and B2C customer acquisition,
        retention and engagement.
      </li>
    </ul>
    <h3>Essential Requirements - About You</h3>
    <ul>
      <li>
        An ethical leader who effectively works with key stakeholders and team
        members to get the best outcome and inspire others to execute the growth
        strategy in a collaborative way.
      </li>
      <li>
        An expert in setting the growth, revenue, pricing and customer success
        strategy in a fast-paced growing startup. Please include a cover letter
        in your application detailing how you have not only helped Startups
        grow, but devised their pricing strategies, conversions from free to
        paid tiers, and community growth and success.
      </li>
      <li>
        Experience in building successful multi-channel enterprise SaaS digital
        engagement and adoption initiatives. Please include a cover letter in
        your application detailing how you have worked in the SaaS space,
        engaging SMEs and Corporates, as well as Consumers / Users, and
        successfully building on a value proposition for each segment.
      </li>
      <li>
        Deep knowledge and demonstrable experience in the digital marketing and
        scalable growth landscapes.
      </li>
      <li>
        Data-driven analytics through a variety of platforms and tools, to
        obtain the data and insights you will need to define and execute the
        growth strategy and related initiatives.
      </li>
      <li>
        Solid understanding of the alphabet soup of digital marketing (SEO, PPC,
        CRO and other exciting acronyms).
      </li>
    </ul>
  </JD>
);

HeadGrowth.propTypes = {
  isExpanded: PropTypes.bool,
  jobPath: PropTypes.string,
};

export const GrowthManager = props => (
  <JD
    title='Digital Community Growth Manager'
    isExpanded={props.isExpanded}
    jobPath={props.jobPath}
    salary='Remote, Flexible'
    key='growthManager'
    customLink={customLinkBuilder('22')}
  >
    <p>
      <b>Reports to</b>: Head of Growth and Monetisation
    </p>
    <p>
      <b>Team</b>: User Engagement and Activation, Content Creators, external
      agencies.
    </p>
    <p>
      <b>Location</b>: Fully remote. Option to work from our offices in Madrid
      (ideal), London and Copenhagen.
    </p>
    <p>
      <b>Travel commitments</b>: trips to our Madrid hub as required.
    </p>
    <p>
      <b>Package</b>: competitive.
    </p>
    <Introduction />
    <h3>The Role</h3>
    <p>
      The <b>Digital Community Growth Manager </b>is the person accountable for
      our organic growth strategy, building our fast-growing community, and
      delivering an outstanding experience to our users.
    </p>
    <p>
      Supporting the growth, revenue and digital success strategy of
      Wikifactory, the Digital Community Manager will be responsible for setting
      and executing initiatives that drive community growth and engagement.
    </p>
    <p>
      You will partner with many cross-functional groups within Wikifactory,
      including Product, Technology, Marketing, Business Intelligence, Sales and
      Partners. Success in this role will require both{' '}
      <i>
        a strong ability to understand the digital customer segment, copywriting
        and producing content, plus the ability to reach out to our community,
        look at trends, and to ask yourself “how can we build upon this?”
      </i>
    </p>
    <p>
      You’ll also influence how we engage our customers in a human, meaningful
      and value-driven way. This is{' '}
      <i>the chance to shape a highly visible area of the business.</i>
    </p>
    <p>
      A background in{' '}
      <i>
        product development, engineering or manufacturing would be extremely
        useful and desired
      </i>
      , along with other essential skills like copywriting, social media, PR,
      SEO, backlinks, PPC and so forth. Organic growth is one of our key drivers
      and maintaining our strong community is essential to achieve our goals.
    </p>
    <h3>Responsibilities</h3>
    <ul>
      <li>
        Define and execute {`Wikifactory's`} organic community growth strategy,
        to drive community engagement and an excellent onboarding experience.
      </li>
      <li>Build organic growth through scalable community content creation.</li>
      <li>
        Identify and maintain an optimal onboarding strategy for customers pre
        and post-signup, driving activity, conversions and engagement throughout
        the community.
      </li>
      <li>
        Execute tests, collect and analyse data and results, as well as identify
        trends and insights of the digital community campaigns and activities.
      </li>
      <li>
        Work with the development team to ensure SEO best practices are properly
        implemented on newly developed code.
      </li>
      <li>
        Manage campaign expenses, staying on budget, estimating monthly costs
        and reconciling discrepancies (in close collaboration with our Marketing
        team).
      </li>
      <li>
        Work with editorial and marketing teams to drive SEO in content creation
        and content programming.
      </li>
      <li>
        Leverage data and analytics to expand and optimise initiatives, ensuring
        a value-led approach is taken across the teams when setting priorities.
        <ul>
          <li>Optimise copy and landing pages for search engine marketing.</li>
          <li>
            Perform ongoing keyword discovery, expansion and optimisation.
          </li>
          <li>
            Research and implement search engine optimisation recommendations.
          </li>
          <li>
            Recommend changes to website architecture, content, linking and
            other factors to improve SEO positions for target keyword.
          </li>
        </ul>
      </li>
      <li>Develop and implement link building strategies.</li>
      <li>
        Foster and strengthen new relationships with key influencers within the
        community.
      </li>
      <li>
        Support the Growth and Marketing teams to set the right level of keyword
        optimisation and other initiatives to drive traffic and engagement.
      </li>
    </ul>
    <h3>Essential Requirements - About You</h3>
    <ul>
      <li>
        An ethical manager with a passion for working with people, and leading
        teams to build and grow our digital community.
      </li>
      <li>
        Experience in building successful multi-channel digital engagement
        initiatives, creating and maintaining a thriving digital community.
        Specifically, please detail in your cover letter how you have engaged
        and attracted new users to join the community... How about keynote
        speakers for webinars and other events? Social media influencers you
        have approached to create a buzz and drive traffic?
      </li>
      <li>
        Design a social media strategy across paid and organic channels, working
        together with our Marketing team to execute it.
      </li>
      <li>
        Deep knowledge and demonstrable experience in the digital marketing and
        scalable growth landscapes.
      </li>
      <li>
        Deep knowledge and demonstrable experience in scalable growth landscape.
      </li>
      <li>
        Solid understanding of the alphabet soup of digital marketing (SEO, PPC,
        CRO and other exciting acronyms).
      </li>
    </ul>
  </JD>
);

GrowthManager.propTypes = {
  isExpanded: PropTypes.bool,
  jobPath: PropTypes.string,
};

export const ContentCreator = props => (
  <JD
    title='Resident Maker & Technical Content Creator'
    isExpanded={props.isExpanded}
    jobPath={props.jobPath}
    salary='Remote, Flexible'
    key='contentCreator'
    customLink={customLinkBuilder('24')}
  >
    <p>
      <b>Reports to</b>: Digital Community Growth Manager
    </p>
    <p>
      <b>Location</b>: Fully remote. Option to work from our offices in Madrid
      (ideal), London and Copenhagen.
    </p>
    <p>
      <b>Travel commitments</b>: trips to our Madrid hub as required.
    </p>
    <p>
      <b>Package</b>: competitive.
    </p>
    <Introduction />
    <p>
      When you write your cover letter, please confirm your experience in
      Physical Product Design / Manufacturing, and share links to articles /
      blog posts you have written in subjects related to the manufacturing /
      Industry 4.0 sector in English.
    </p>
    <h3>The Role</h3>
    <p>
      The <b>Resident Maker & Technical Content Creator </b>is passionate about
      our mission and the opportunity to reshape the future of design and
      production.
      <b>
        {' '}
        You will contribute in raising the profile of Wikifactory as a source of
        inspirational and technical content in the fields of physical product
        design and fabrication.
      </b>
    </p>
    <p>
      We are at an exciting growth phase where we seek to further host{' '}
      <b>
        niche content communities that are emerging on Wikifactory with the
        greatest community traction
      </b>{' '}
      whether in terms of{' '}
      <b>
        fabrication technologies, like metal 3D printing, or product verticals
        like drones, as well as themes like social impact
      </b>
      . We have built a strong and loyal global community and would like to see
      this grow with the curation and development of content related to the
      trends, technical advancements and inspirations of our sector.
    </p>
    <p>
      You should be able to work independently and collaboratively engage with
      our community and the broader network of experts and thought leaders
      relevant to Wikifactory to <b>create rich highly technical content</b>{' '}
      that indexes high on Google and is search engine optimised with relevant
      keywords and with headlines that reflect prominent search queries.
    </p>
    <p>
      <b>
        The role plays an important part in attracting and activating users and
        key stakeholders, and it contributes significantly to our performance,
        growth and user engagement targets.
      </b>
    </p>
    <h3>Responsibilities</h3>
    <Heading level={4} size='small'>
      Physical Product Technical Content
    </Heading>
    <ul>
      <li>
        Inspire the community with cornerstone and technical articles that can
        help them advance in their physical product development. Engage the
        community by interacting across the platform through the use of our
        features: stories, forum threads and issues within projects.
      </li>
      <li>
        Develop and publish inspiring and technical content of reference in
        topics related to the latest trends, strategic developments and the most
        important news in physical product design and fabrication.
      </li>
      <li>
        Research and create content to support our Communications, Business and
        Community development efforts.
      </li>
      <li>
        Curate user-generated content (stories/blog posts and rich content such
        as images, videos, graphics) and coordinate with the Communications and
        Community teams to support the promotion of this content.
      </li>
    </ul>
    <Heading level={4} size='small'>
      Community
    </Heading>
    <ul>
      <li>
        Curate and activate a distributed community of bloggers, experts and key
        opinion leaders on Wikifactory. They are empowered with our publishing
        and social collaboration tools to share their perspectives and engage in
        meaningful discussion.
      </li>
      <li>
        Engage super users and experts/thought leaders in new communities of
        interest and of practice on their social media channels and through
        personal social accounts and LinkedIn groups.
      </li>
      <li>
        Contribute to company-wide efforts to stay informed on the latest and
        most relevant maker and product design trends, enhancing insights for
        campaigns across different functional teams with a deeper understanding
        of the market we serve.
      </li>
    </ul>
    <h3>Essential Requirements - About You</h3>
    <ul>
      <li>
        Physical product design/manufacturing expertise and/or Industrial
        Engineering.
      </li>
      <li>
        English C1-2 Level (native or near-native). All content will be written
        in English and then translated to other languages, so it’s important
        that your written English is flawless.
      </li>
      <li>
        Excellent communicator, with a passion for words and crafting impactful
        and value-add technical content.
      </li>
      <li>
        Demonstrated ability to explain complex technical topics in a clear and
        accessible way.
      </li>
      <li>Experience in SEO content development.</li>
      <li>
        Relationships with product development and engineering communities, as
        well as relevant trade media.
      </li>
      <li>Experience in data-driven communications and community building.</li>
      <li>
        Please provide links to your portfolio of articles/blog posts relevant
        to this role.
      </li>
    </ul>
  </JD>
);

ContentCreator.propTypes = {
  isExpanded: PropTypes.bool,
  jobPath: PropTypes.string,
};
